// V5: Diya flame glows from bottom center, card rises up
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill, spring } from "remotion";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: inter } = loadInter("normal", { weights: ["600", "700", "800"], subsets: ["latin"] });

const DiyaSVG: React.FC<{ flicker: number }> = ({ flicker }) => (
  <svg width="40" height="47" viewBox="0 0 60 70" fill="none">
    <ellipse cx="30" cy="18" rx={6 + flicker} ry={10 + flicker * 0.5} fill="#FF6B00" opacity="0.9" />
    <ellipse cx="30" cy="20" rx={4 + flicker * 0.5} ry={7} fill="#FFD700" />
    <ellipse cx="30" cy="23" rx="2" ry="4" fill="#fff" opacity="0.8" />
    <line x1="30" y1="28" x2="30" y2="33" stroke="#333" strokeWidth="1.5" />
    <path d="M10 38 Q15 28 30 30 Q45 28 50 38 Q50 50 30 52 Q10 50 10 38Z" fill="#C8A96E" stroke="#8B6914" strokeWidth="1.5" />
    <path d="M10 38 Q15 34 30 36 Q45 34 50 38" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    <ellipse cx="30" cy="36" rx="10" ry="3" fill="rgba(255,200,0,0.3)" />
    <ellipse cx="30" cy="22" rx={18 + flicker * 2} ry={14 + flicker} fill="rgba(255,150,0,0.08)" />
  </svg>
);

const ThumbUpSVG: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 64 64" fill="none">
    <path d="M20 28L28 8C30 8 34 9 34 16V24H52C54 24 56 26 55 30L50 52C49 54 47 55 45 55H20V28Z" fill={color} />
    <rect x="8" y="28" width="12" height="28" rx="3" fill={color} />
  </svg>
);

export const DevaKathaLS5: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const flicker = Math.sin(frame * 1.8) * 1.2 + Math.sin(frame * 3.1) * 0.6;

  const diyaScale = spring({ frame, fps, config: { damping: 12, stiffness: 100 }, durationInFrames: Math.round(0.8 * fps) });
  const diyaS = interpolate(diyaScale, [0, 1], [0, 1]);

  const cardY = interpolate(frame, [Math.round(0.7 * fps), Math.round(1.3 * fps)], [20, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const cardOpacity = interpolate(frame, [Math.round(0.7 * fps), Math.round(1.2 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });

  const likeClicked = frame >= Math.round(1.8 * fps);
  const subClicked = frame >= Math.round(2.6 * fps);

  const likeScale = interpolate(frame, [Math.round(1.8 * fps), Math.round(1.87 * fps), Math.round(2.0 * fps)], [1, 0.86, 1.07], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const subScale = interpolate(frame, [Math.round(2.6 * fps), Math.round(2.67 * fps), Math.round(2.8 * fps)], [1, 0.86, 1.07], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  const fadeOut = interpolate(frame, [Math.round(3.5 * fps), Math.round(4 * fps)], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: fadeOut }}>
      <div style={{ position: "absolute", bottom: 33, left: "50%", transform: "translateX(-50%) scale(1.5)", transformOrigin: "bottom center", display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
        <div style={{ transform: `scale(${diyaS})`, filter: "drop-shadow(0 0 20px rgba(255,150,0,0.8))" }}>
          <DiyaSVG flicker={flicker} />
        </div>
        <div style={{
          opacity: cardOpacity, transform: `translateY(${cardY}px)`,
          background: "rgba(10,8,4,0.88)", backdropFilter: "blur(12px)",
          borderRadius: 12, padding: "8px 19px",
          border: "1px solid rgba(255,150,0,0.4)",
          boxShadow: "0 8px 32px rgba(255,100,0,0.2)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 7, minWidth: 187,
        }}>
          <span style={{ color: "rgba(255,200,100,0.8)", fontSize: 7, fontWeight: 600, fontFamily: inter, letterSpacing: 1.5, textTransform: "uppercase" }}>Deva Katha</span>
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              background: likeClicked ? "#1a73e8" : "rgba(255,255,255,0.1)",
              border: `1px solid ${likeClicked ? "transparent" : "rgba(255,255,255,0.15)"}`,
              borderRadius: 15, padding: "6px 0", transform: `scale(${likeScale})`,
              boxShadow: likeClicked ? "0 4px 14px rgba(26,115,232,0.5)" : "none",
            }}>
              <ThumbUpSVG color="#fff" />
              <span style={{ color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: inter, whiteSpace: "nowrap" }}>{likeClicked ? "Liked!" : "Like"}</span>
            </div>
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              background: subClicked ? "#606060" : "#FF0000",
              borderRadius: 15, padding: "6px 0", transform: `scale(${subScale})`,
              boxShadow: subClicked ? "none" : "0 4px 18px rgba(255,0,0,0.5)",
            }}>
              <span style={{ color: "#fff", fontSize: 10, fontWeight: 800, fontFamily: inter, whiteSpace: "nowrap" }}>{subClicked ? "Subscribed ✓" : "Subscribe"}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
